import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';
import { getCardScrollTarget, SCROLL_TOLERANCE } from './loomi-carousel-utils.js';

/**
 * How long a programmatic smooth scroll is given to land before the component
 * finishes the move instantly and restores snapping.
 */
const SCROLL_SETTLE_TIMEOUT = 600;

/**
 * Chevron navigation and scroll progress for a Bloomreach Loomi
 * recommendations carousel rendered in the same section.
 *
 * The Loomi widget renders its heading and `.br-products__grid` scroll
 * container in the light DOM but ships no navigation controls. This
 * component overlays previous/next chevrons on the widget heading row and a
 * progress bar under the cards, and drives the widget's own scroll
 * container. It stays hidden until the widget grid mounts.
 *
 * @typedef {Object} Refs
 * @property {HTMLButtonElement} previousButton
 * @property {HTMLButtonElement} nextButton
 * @property {HTMLElement} [progress]
 * @property {HTMLElement} [progressThumb]
 *
 * @extends {Component<Refs>}
 */
class LoomiCarouselControlsComponent extends Component {
  requiredRefs = ['previousButton', 'nextButton'];

  /** @type {HTMLElement | null} */
  #grid = null;

  /** @type {MutationObserver | null} */
  #mountObserver = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {number} */
  #settleTimer = 0;

  /** @type {number} */
  #animationFrame = 0;

  /** @type {number} */
  #scrollTimer = 0;

  /** @type {boolean} */
  #interrupted = false;

  /**
   * The offset an in-flight programmatic scroll is heading for, so a second
   * arrow press steps on from there rather than from the half-finished
   * position and repeats the same card.
   *
   * @type {number | null}
   */
  #pendingTarget = null;

  #handlePrevious = () => this.previous();

  #handleNext = () => this.next();

  connectedCallback() {
    super.connectedCallback();

    // Bloomreach injects this component after the theme's delegated event
    // registry has initialised. Bind the controls directly so they work for
    // both late-injected Loomi markup and the theme block fallback.
    this.refs.previousButton.removeAttribute('on:click');
    this.refs.nextButton.removeAttribute('on:click');
    this.refs.previousButton.addEventListener('click', this.#handlePrevious);
    this.refs.nextButton.addEventListener('click', this.#handleNext);
    this.#connectToGrid();
  }

  disconnectedCallback() {
    this.#teardown();
    super.disconnectedCallback();
  }

  /**
   * Scrolls the carousel one card back.
   */
  previous() {
    this.#scrollByStep(-1);
  }

  /**
   * Scrolls the carousel one card forward.
   */
  next() {
    this.#scrollByStep(1);
  }

  #connectToGrid() {
    const scope = this.closest('.br-carousel-layout') || this.closest('.shopify-section');
    if (!scope) return;

    this.#adopt(scope);

    // The widget swaps its skeleton grid for the real grid, then streams
    // cards into it — neither changes the grid's own box, so a
    // ResizeObserver alone misses them. Keep observing: re-adopt when the
    // grid element changes and refresh state as cards land.
    this.#mountObserver = new MutationObserver(() => {
      this.#adopt(scope);
      this.#queueUpdate();
    });
    this.#mountObserver.observe(scope, { childList: true, subtree: true });
  }

  /**
   * Adopts the widget's scroll grid once it has rendered product cards,
   * re-binding if the widget has replaced the grid element.
   *
   * @param {Element} scope - The carousel layout or section containing the widget.
   */
  #adopt(scope) {
    const grid = scope.querySelector('.br-products__grid:not(.br-products__grid--skeleton)');
    if (!(grid instanceof HTMLElement) || grid.childElementCount === 0) return;
    if (grid === this.#grid) return;

    this.#grid?.removeEventListener('scroll', this.#queueUpdate);
    if (this.#grid) this.#releaseSnap(this.#grid);
    window.clearTimeout(this.#scrollTimer);
    this.#resizeObserver?.disconnect();

    this.#grid = grid;
    grid.tabIndex = 0;
    grid.setAttribute('role', 'region');
    const heading = scope.closest('.br-recommendations')?.querySelector('.br-recommendations__heading');
    grid.setAttribute('aria-label', heading?.textContent?.trim() || 'Product carousel');
    grid.addEventListener('scroll', this.#queueUpdate, { passive: true });
    this.#resizeObserver = new ResizeObserver(this.#queueUpdate);
    this.#resizeObserver.observe(grid);
    this.removeAttribute('data-hidden');
    this.#update();
    this.#settle();
  }

  /**
   * The widget lays out its cards after the last DOM mutation (custom
   * elements upgrade, images size in), which changes the grid's scroll
   * range without firing any observer. Re-check on an interval until the
   * scroll range stops changing.
   */
  #settle() {
    clearInterval(this.#settleTimer);
    let previousSize = '';
    let stableTicks = 0;

    this.#settleTimer = window.setInterval(() => {
      const grid = this.#grid;
      if (!grid || stableTicks >= 8) {
        clearInterval(this.#settleTimer);
        return;
      }

      const size = `${grid.scrollWidth}x${grid.scrollHeight}`;
      if (size === previousSize) {
        stableTicks += 1;
      } else {
        stableTicks = 0;
        previousSize = size;
        this.#update();
      }
    }, 250);
  }

  #teardown() {
    clearInterval(this.#settleTimer);
    window.clearTimeout(this.#scrollTimer);
    cancelAnimationFrame(this.#animationFrame);
    if (this.#grid) this.#releaseSnap(this.#grid);
    this.refs.previousButton?.removeEventListener('click', this.#handlePrevious);
    this.refs.nextButton?.removeEventListener('click', this.#handleNext);
    this.#mountObserver?.disconnect();
    this.#mountObserver = null;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#grid?.removeEventListener('scroll', this.#queueUpdate);
    this.#grid = null;
  }

  /**
   * The scroll offsets of the rendered cards, in document order.
   *
   * `offsetLeft` is used rather than `getBoundingClientRect()`: it is
   * scroll-independent and integral, where the rect returns fractional values
   * that drift as the row scrolls.
   *
   * @returns {number[]}
   */
  get #cardOffsets() {
    const grid = this.#grid;
    if (!grid) return [];

    // The widget leaves collapsed wrappers in the row (no box, no offset
    // parent); their offsetLeft of 0 would corrupt the card starts.
    return Array.from(grid.children)
      .filter((child) => child instanceof HTMLElement && child.offsetParent !== null)
      .map((card) => /** @type {HTMLElement} */ (card).offsetLeft);
  }

  /**
   * @param {number} direction - 1 for forward, -1 for back.
   */
  #scrollByStep(direction) {
    const grid = this.#grid;
    if (!grid) return;

    // Right-to-left carousels report scroll offsets differently across
    // engines, so they keep the relative step rather than an absolute target.
    if (getComputedStyle(grid).direction === 'rtl') {
      const [first, second] = grid.children;
      const step =
        first instanceof HTMLElement && second instanceof HTMLElement
          ? Math.abs(second.offsetLeft - first.offsetLeft)
          : grid.clientWidth;
      grid.scrollBy({ left: -direction * step, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
      return;
    }

    const target = getCardScrollTarget(
      {
        offsets: this.#cardOffsets,
        scrollLeft: this.#pendingTarget ?? grid.scrollLeft,
        maxScroll: grid.scrollWidth - grid.clientWidth,
      },
      direction
    );

    // Fewer than two cards: nothing to step between, so move by a viewport.
    if (target === null) {
      if (this.#cardOffsets.length >= 2) return;
      grid.scrollBy({ left: direction * grid.clientWidth, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
      return;
    }

    if (prefersReducedMotion()) {
      grid.scrollTo({ left: target, behavior: 'auto' });
      return;
    }

    this.#smoothScrollTo(grid, target);
  }

  /**
   * Scrolls to an absolute offset with snapping suspended for the animation.
   *
   * iOS Safari cancels a programmatic smooth scroll inside a
   * `scroll-snap-type: mandatory` container almost immediately and re-snaps to
   * the nearest point — the card the shopper started on. Reported on
   * iPhone 17 Pro / iOS 26 as the arrow nudging the row a few pixels and
   * stopping (SM-527); emulators and desktop Safari do not reproduce it.
   * Suspending snap lets the animation land, then it is restored so touch
   * flicks keep snapping.
   *
   * @param {HTMLElement} grid - The widget's scroll container.
   * @param {number} target - Absolute scroll offset to land on.
   */
  #smoothScrollTo(grid, target) {
    window.clearTimeout(this.#scrollTimer);
    this.#interrupted = false;
    this.#pendingTarget = target;
    grid.style.scrollSnapType = 'none';
    grid.addEventListener('pointerdown', this.#interrupt, { once: true });
    grid.addEventListener('touchstart', this.#interrupt, { once: true, passive: true });
    grid.addEventListener('wheel', this.#interrupt, { once: true, passive: true });
    grid.scrollTo({ left: target, behavior: 'smooth' });

    this.#scrollTimer = window.setTimeout(() => {
      if (grid !== this.#grid) return;

      // A cancelled animation leaves the row short of the target; finish the
      // move so the arrow always advances exactly one card. A shopper who
      // took over mid-animation keeps their position.
      if (!this.#interrupted && Math.abs(grid.scrollLeft - target) > SCROLL_TOLERANCE) {
        grid.scrollTo({ left: target, behavior: 'auto' });
      }
      this.#releaseSnap(grid);
    }, SCROLL_SETTLE_TIMEOUT);
  }

  #interrupt = () => {
    this.#interrupted = true;
    // The shopper is driving now; the queued target no longer describes where
    // the row is going, so the next arrow press steps from the real position.
    this.#pendingTarget = null;
  };

  /**
   * Restores the container's own snapping and drops the interruption guards.
   * @param {HTMLElement} grid - The widget's scroll container.
   */
  #releaseSnap(grid) {
    this.#pendingTarget = null;
    grid.removeEventListener('pointerdown', this.#interrupt);
    grid.removeEventListener('touchstart', this.#interrupt);
    grid.removeEventListener('wheel', this.#interrupt);
    grid.style.scrollSnapType = '';
  }

  #queueUpdate = () => {
    cancelAnimationFrame(this.#animationFrame);
    this.#animationFrame = requestAnimationFrame(this.#update);
  };

  #update = () => {
    const grid = this.#grid;
    if (!grid) return;

    const maxScroll = grid.scrollWidth - grid.clientWidth;
    const scrollPosition = getComputedStyle(grid).direction === 'rtl' ? Math.abs(grid.scrollLeft) : grid.scrollLeft;
    const hasOverflow = maxScroll > 1;
    this.toggleAttribute('data-no-overflow', !hasOverflow);
    this.refs.previousButton.disabled = !hasOverflow || scrollPosition <= 1;
    this.refs.nextButton.disabled = !hasOverflow || scrollPosition >= maxScroll - 1;

    const anchor = grid.getBoundingClientRect().bottom - this.getBoundingClientRect().top;
    this.style.setProperty('--na-progress-anchor', `${anchor}px`);

    const { progress, progressThumb } = this.refs;
    if (!(progress instanceof HTMLElement) || !(progressThumb instanceof HTMLElement)) return;

    if (!hasOverflow) {
      progress.setAttribute('data-hidden', '');
      return;
    }

    progress.removeAttribute('data-hidden');
    const track = progress.clientWidth;
    progressThumb.style.setProperty('--na-progress-x', `${(scrollPosition / grid.scrollWidth) * track}px`);
    progressThumb.style.setProperty('--na-progress-scale', `${grid.clientWidth / grid.scrollWidth}`);
  };
}

if (!customElements.get('loomi-carousel-controls-component')) {
  customElements.define('loomi-carousel-controls-component', LoomiCarouselControlsComponent);
}
